import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';

import { AppProfileType } from '../../../application';
import { ConnectionsService } from '../../../application/services/connections.service';
import { DrawerService } from '../drawer.service';
import { LanguageSelector } from '../../shared/language-selector';
import { McAvatar } from '../../shared/avatar';
import { McIcon } from 'makeclub-ui';
import { ProfileService } from '../../../application/services/profile.service';
import { StarRating } from '../../shared/star-rating';

@Component({
  selector: 'mc-sidebar',
  template: `
    <nav
      class="text-text-primary flex h-full min-w-80 flex-col border-neutral-100 bg-black shadow md:border-r md:shadow-2xl"
    >
      <header class="relative flex items-center justify-between gap-2 bg-white px-4 py-4">
        <button
          class="absolute top-1 right-1 p-1 text-2xl text-gray-500 md:hidden"
          (click)="drawer.close()"
          [attr.aria-label]="'nav.closeMenu' | transloco"
        >
          <mc-icon name="x" />
        </button>

        <div class="relative flex size-24 items-center justify-center">
          <mc-avatar
            [picture]="profilePicture()"
            [name]="nombre()"
            [type]="profileType()"
            [verified]="isVerified()"
            [status]="profileStatus()"
            size="lg"
          />
        </div>

        <div class="flex flex-col items-start justify-evenly gap-2 p-2">
          <span class="text-lg font-bold">{{ nombre() }}</span>
          @if (alias()) {
            <span class="text-text-secondary text-lg">{{ alias() }}</span>
          }

          @if (rate()) {
            <mc-star-rating [rating]="rate()" />
          }

          <div class="flex gap-2 text-sm">
            <p>
              <span class="font-bold">{{ followersCount() }}</span>
              {{ 'nav.followers' | transloco }}
            </p>
            <p>
              <span class="font-bold">{{ followingCount() }}</span>
              {{ 'nav.following' | transloco }}
            </p>
          </div>
        </div>
      </header>
      <div class="flex grow flex-col justify-between p-8 text-xl text-white">
        <ul class="flex flex-col gap-6">
          <li class="flex items-center gap-4">
            <span class="text-brand-primary text-3xl">
              <mc-icon name="user" />
            </span>
            <a
              class="text-md md:text-lg"
              routerLink="profile"
              routerLinkActive="font-bold"
              (click)="drawer.close()"
            >
              {{ 'nav.myProfile' | transloco }}
            </a>
          </li>
          <div class="border border-neutral-600"></div>
          <li class="flex items-center gap-6">
            <span class="text-brand-primary text-3xl">
              <mc-icon name="help" />
            </span>
            <a href="mailto:support@makeclub.app" class="text-md md:text-lg">
              {{ 'nav.help' | transloco }}
            </a>
          </li>
        </ul>

        <div class="pt-6">
          <mc-language-selector />
        </div>
      </div>
    </nav>
  `,
  imports: [McIcon, RouterLink, McAvatar, StarRating, RouterLinkActive, TranslocoModule, LanguageSelector],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideBar {
  protected readonly drawer = inject(DrawerService);
  private readonly profileService = inject(ProfileService);
  private readonly connectionsService = inject(ConnectionsService);

  private readonly profile = this.profileService.myProfile;
  private readonly connections = this.connectionsService.connections;

  protected readonly nombre = computed(() => this.profile.data()?.nombre ?? '');
  protected readonly alias = computed(() => this.profile.data()?.alias);
  protected readonly isVerified = computed(() => this.profile.data()?.verified ?? false);
  protected readonly profileType = computed(() => this.profile.data()?.type);
  protected readonly profileStatus = computed(() => {
    const profile = this.profile.data();
    if (
      profile?.type === AppProfileType.Player ||
      profile?.type === AppProfileType.Coach ||
      profile?.type === AppProfileType.Staff
    ) {
      return profile.status;
    }
    return undefined;
  });
  protected readonly profilePicture = computed(() => this.profile.data()?.profilePicture);
  protected readonly followersCount = computed(() => this.connections.data()?.followers.length ?? 0);
  protected readonly followingCount = computed(() => this.connections.data()?.following.length ?? 0);
  protected readonly rate = computed(() => this.profile.data()?.rate);
}
